import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import type { ChatMessageDeltaDto, ChatMessageDto } from '../types/api';
import { resolveApiBaseUrl } from './api';

type DeltaHandler = (delta: ChatMessageDeltaDto) => void;
type UpdatedHandler = (message: ChatMessageDto) => void;
type ReconnectedHandler = () => void;

/**
 * Resolve the SignalR hub URL by stripping the trailing `/api` segment from the configured
 * Axios base URL — the hub is mounted at `/hubs/chat` on the app root, not under `/api`.
 */
function resolveHubUrl(): string {
  const root = resolveApiBaseUrl().replace(/\/api\/?$/, '');
  return `${root}/hubs/chat`;
}

const SERVER_EVENT_DELTA = 'MessageDelta';
const SERVER_EVENT_UPDATED = 'MessageUpdated';

let connection: HubConnection | null = null;
let starting: Promise<void> | null = null;
const deltaHandlers = new Set<DeltaHandler>();
const updatedHandlers = new Set<UpdatedHandler>();
const reconnectedHandlers = new Set<ReconnectedHandler>();

function ensureConnection(): HubConnection {
  if (connection) return connection;

  const c = new HubConnectionBuilder()
    .withUrl(resolveHubUrl(), {
      accessTokenFactory: () => localStorage.getItem('authToken') ?? '',
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  c.on(SERVER_EVENT_DELTA, (delta: ChatMessageDeltaDto) => {
    deltaHandlers.forEach((h) => {
      try {
        h(delta);
      } catch (e) {
        console.error('chatHub delta handler threw:', e);
      }
    });
  });

  c.on(SERVER_EVENT_UPDATED, (message: ChatMessageDto) => {
    updatedHandlers.forEach((h) => {
      try {
        h(message);
      } catch (e) {
        console.error('chatHub updated handler threw:', e);
      }
    });
  });

  c.onreconnected(() => {
    reconnectedHandlers.forEach((h) => {
      try {
        h();
      } catch (e) {
        console.error('chatHub reconnected handler threw:', e);
      }
    });
  });

  connection = c;
  return c;
}

/**
 * Connects to the chat hub if not already connected. Safe to call repeatedly: subsequent calls
 * return the in-flight `start()` promise so handlers attached before connection finishes still
 * receive events.
 */
export async function connectChatHub(): Promise<void> {
  const c = ensureConnection();
  if (c.state === HubConnectionState.Connected) return;
  if (starting) return starting;
  starting = c.start().catch((err) => {
    console.error('Failed to start chat hub connection:', err);
    starting = null;
    throw err;
  });
  try {
    await starting;
  } finally {
    starting = null;
  }
}

/**
 * Tears down the active connection. Handler subscriptions are NOT cleared so reconnecting later
 * starts streaming again immediately; call the corresponding `off*` to fully unsubscribe.
 */
export async function disconnectChatHub(): Promise<void> {
  if (!connection) return;
  const c = connection;
  connection = null;
  try {
    await c.stop();
  } catch (e) {
    console.warn('chatHub stop threw:', e);
  }
}

export function onMessageDelta(handler: DeltaHandler): void {
  deltaHandlers.add(handler);
}

export function offMessageDelta(handler: DeltaHandler): void {
  deltaHandlers.delete(handler);
}

export function onMessageUpdated(handler: UpdatedHandler): void {
  updatedHandlers.add(handler);
}

export function offMessageUpdated(handler: UpdatedHandler): void {
  updatedHandlers.delete(handler);
}

export function onReconnected(handler: ReconnectedHandler): void {
  reconnectedHandlers.add(handler);
}

export function offReconnected(handler: ReconnectedHandler): void {
  reconnectedHandlers.delete(handler);
}
